/**
 * テナント/労基ルールページ (/tenant/labor-rule)
 *
 * 区分（標準/未成年/留学生）×種類（所定労働時間/労基チェック）ごとの労基ルール設定。
 * 現状はモックデータの一覧表示のみ。
 */
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { TabBar } from "../components/molecules/TabBar";
import {
    CommonTable,
    CommonTableHeaderCell,
    CommonTableEditHeaderCell,
    CommonTableRow,
    CommonTableCell,
    CommonTableEditCell,
} from "../components/table";
import { useMenuTitle } from "../shared/menu/useMenuTitle";
import {
    MOCK_LABOR_RULE_ROWS,
    formatHourMinute,
    type LaborRuleRow,
} from "./workRulesMockData";

const MULTI_LINE_CELL_CLASS = "!h-auto py-3 text-left whitespace-normal align-top";

interface DetailLine {
    label: string;
    value: string;
}

const buildDetailLines = (row: LaborRuleRow): DetailLine[] => {
    if (row.kind === "所定労働時間" && row.scheduledWorkHours) {
        const { perDay, perWeek } = row.scheduledWorkHours;
        return [
            { label: "1日あたり", value: formatHourMinute(perDay.hour, perDay.minute) },
            { label: "1週間あたり", value: formatHourMinute(perWeek.hour, perWeek.minute) },
        ];
    }
    if (row.kind === "労基チェック" && row.laborCheck) {
        const { perDay, perWeek, perMonth, consecutiveDays } = row.laborCheck;
        const lines: DetailLine[] = [
            { label: "1日あたり", value: formatHourMinute(perDay.hour, perDay.minute) },
            { label: "1週間あたり", value: formatHourMinute(perWeek.hour, perWeek.minute) },
        ];
        if (perMonth) {
            lines.push({
                label: "1か月あたり",
                value: formatHourMinute(perMonth.hour, perMonth.minute),
            });
        }
        if (consecutiveDays != null) {
            lines.push({ label: "連勤日数", value: `${consecutiveDays}日` });
        }
        return lines;
    }
    return [];
};

const WorkRulesSettingsPage = () => {
    const menuTitle = useMenuTitle();
    const navigate = useNavigate();

    const handleEditRow = (row: LaborRuleRow) => {
        navigate(`/tenant/labor-rule/${row.id}/edit`);
    };

    return (
        <div className="flex flex-1 flex-col overflow-hidden">
            <PageHeader title={menuTitle} />
            <TabBar parentCode="tenant" />

            <div className="flex-1 overflow-hidden px-6 pb-6">
                <CommonTable>
                    <thead className="sticky top-0 z-20">
                        <tr>
                            <CommonTableEditHeaderCell />
                            <CommonTableHeaderCell className="min-w-[120px]">区分</CommonTableHeaderCell>
                            <CommonTableHeaderCell className="min-w-[160px]">種類</CommonTableHeaderCell>
                            <CommonTableHeaderCell className="min-w-[120px]">ステータス</CommonTableHeaderCell>
                            <CommonTableHeaderCell className="min-w-[140px]">変更フラグ</CommonTableHeaderCell>
                            <CommonTableHeaderCell className="min-w-[260px]">設定内容</CommonTableHeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        {MOCK_LABOR_RULE_ROWS.map((row) => (
                            <CommonTableRow key={row.id}>
                                <CommonTableEditCell onClick={() => handleEditRow(row)} />
                                <CommonTableCell>{row.category}</CommonTableCell>
                                <CommonTableCell>{row.kind}</CommonTableCell>
                                <CommonTableCell>{row.status}</CommonTableCell>
                                <CommonTableCell>{row.changeFlag}</CommonTableCell>
                                <CommonTableCell className={MULTI_LINE_CELL_CLASS}>
                                    {buildDetailLines(row).map((detail) => (
                                        <div
                                            key={detail.label}
                                            className="flex items-baseline gap-2 leading-6"
                                        >
                                            <span className="text-gray-500">{detail.label}</span>
                                            <span>{detail.value}</span>
                                        </div>
                                    ))}
                                </CommonTableCell>
                            </CommonTableRow>
                        ))}
                    </tbody>
                </CommonTable>
            </div>
        </div>
    );
};

export default WorkRulesSettingsPage;
